import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomAlert from "../components/CustomAlert";

export default function Collections() {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: '',
    fabric: '',
    price: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [alertConfig, setAlertConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: null,
    onCancel: null
  });
  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://teenagefashion.in/backend/api' 
      : 'http://localhost/newfashion/backend/api');

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/collections.php`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch collections");
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCollections(data.collections || []);
      } else {
        throw new Error(data.error || "Failed to fetch collections");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleAddNew = () => {
    navigate("/latest-collections");
  };

  const handleEdit = (collection) => {
    setEditingCollection(collection);
    setEditFormData({
      title: collection.title,
      fabric: collection.fabric,
      price: collection.price
    });
    setSelectedImage(null);
    setImagePreview(null);
    setEditModalOpen(true);
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditingCollection(null);
    setEditFormData({
      title: '',
      fabric: '',
      price: ''
    });
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleEditSave = async () => {
    if (!editingCollection) return;
    
    try {
      const formData = new FormData();
      formData.append('id', editingCollection.id);
      formData.append('title', editFormData.title);
      formData.append('fabric', editFormData.fabric);
      formData.append('price', editFormData.price);
      
      // Add image if selected
      if (selectedImage) {
        formData.append('image', selectedImage);
      }
      
      const response = await fetch(`${API_URL}/edit_collections.php`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update the collections list with the edited collection
        setCollections(prevCollections => 
          prevCollections.map(collection => 
            collection.id === editingCollection.id 
              ? { 
                  ...collection, 
                  ...editFormData,
                  image: data.collection.image // Use the new image path from API response
                }
              : collection
          )
        );
        handleEditCancel();
        // Show success message
        setAlertConfig({
          isOpen: true,
          title: 'Success',
          message: 'Collection updated successfully',
          type: 'success',
          onConfirm: () => setAlertConfig(prev => ({ ...prev, isOpen: false })),
          onCancel: null
        });
      } else {
        setAlertConfig({
          isOpen: true,
          title: 'Error',
          message: data.error || 'Failed to update collection',
          type: 'error',
          onConfirm: () => setAlertConfig(prev => ({ ...prev, isOpen: false })),
          onCancel: null
        });
      }
    } catch (error) {
      console.error('Error updating collection:', error);
      setAlertConfig({
        isOpen: true,
        title: 'Error',
        message: 'Failed to update collection. Please try again.',
        type: 'error',
        onConfirm: () => setAlertConfig(prev => ({ ...prev, isOpen: false })),
        onCancel: null
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDelete = async (collection) => {
    setAlertConfig({
      isOpen: true,
      title: 'Delete Collection',
      message: `Are you sure you want to delete "${collection.title}"? This action cannot be undone.`,
      type: 'confirm',
      onConfirm: async () => {
        try {
          const formData = new FormData();
          formData.append('id', collection.id);
          
          const response = await fetch(`${API_URL}/delete_collection.php`, {
            method: 'POST',
            credentials: 'include',
            body: formData
          });
          
          const data = await response.json();
          
          if (data.success) {
            // Remove the collection from the list
            setCollections(prevCollections => 
              prevCollections.filter(c => c.id !== collection.id)
            );
            // Show success message
            setAlertConfig({
              isOpen: true,
              title: 'Success',
              message: 'Collection deleted successfully',
              type: 'success',
              onConfirm: () => setAlertConfig(prev => ({ ...prev, isOpen: false })),
              onCancel: null
            });
          } else {
            setAlertConfig({
              isOpen: true,
              title: 'Error',
              message: data.error || 'Failed to delete collection',
              type: 'error',
              onConfirm: () => setAlertConfig(prev => ({ ...prev, isOpen: false })),
              onCancel: null
            });
          }
        } catch (error) {
          console.error('Error deleting collection:', error);
          setAlertConfig({
            isOpen: true,
            title: 'Error',
            message: 'Failed to delete collection. Please try again.',
            type: 'error',
            onConfirm: () => setAlertConfig(prev => ({ ...prev, isOpen: false })),
            onCancel: null
          });
        }
      },
      onCancel: () => setAlertConfig(prev => ({ ...prev, isOpen: false }))
    });
  };

  return (
    <div className="max-w-screen w-full mx-auto overflow-x-hidden">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Collections</h1>
            <p className="text-gray-600 mt-1">Manage your fabric collections and designs</p>
          </div>
          <button
            onClick={handleAddNew}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
          >
            <span>+</span>
            <span>Add New Collection</span>
          </button>
        </div>
      </div>

      {/* Collections Table */}
      <div className="bg-white rounded-xl shadow p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold mb-4 text-gray-800">All Collections</h2>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="text-gray-500">Loading collections...</div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <div className="text-red-500 mb-4">{error}</div>
            <button
              onClick={fetchCollections}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        ) : collections.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-4">No collections found.</div>
            <button
              onClick={handleAddNew}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
            >
              Add Your First Collection
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-2 font-semibold text-gray-700">Image</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-700">Title</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-700">Fabric</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-700">Price</th>
                  <th className="text-left py-3 px-2 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {collections.map((collection) => (
                  <tr key={collection.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                                         <td className="py-3 px-2">
                       <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                         <img
                           src={process.env.NODE_ENV === 'production' 
                             ? `https://teenagefashion.in/${collection.image}`
                             : `http://localhost/newfashion/${collection.image}`
                           }
                           alt={collection.title}
                           className="w-full h-full object-cover"
                           onError={(e) => {
                             e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAzNkMzNC4yMDkxIDM2IDM2IDM0LjIwOTEgMzYgMzJDMzYgMjkuNzkwOSAzNC4yMDkxIDI4IDMyIDI4QzI5Ljc5MDkgMjggMjggMjkuNzkwOSAyOCAzMkMyOCAzNC4yMDkxIDI5Ljc5MDkgMzYgMzIgMzZaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0yMCA0OEg0NFY0NEM0NCA0Mi44OTU0IDQzLjEwNDYgNDIgNDIgNDJIMjJDMjAuODk1NCA0MiAyMCA0Mi44OTU0IDIwIDQ0VjQ4WiIgZmlsbD0iIzlCOUJBMCIvPgo8L3N2Zz4K';
                           }}
                         />
                       </div>
                     </td>
                    <td className="py-3 px-2">
                      <div className="font-medium text-gray-900">{collection.title}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="text-gray-700">{collection.fabric}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="font-medium text-green-600">{formatPrice(collection.price)}</div>
                    </td>
                    <td className="py-3 px-2">
                      <div className="flex flex-col gap-0.5">
                        <button
                          onClick={() => handleEdit(collection)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded text-xs font-medium transition-colors duration-200 sm:px-1 sm:w-12"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(collection)}
                          className="bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded text-xs font-medium transition-colors duration-200 sm:px-1 sm:w-12"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Edit Collection</h3>
                <button
                  onClick={handleEditCancel}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={(e) => { e.preventDefault(); handleEditSave(); }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={editFormData.title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fabric
                    </label>
                    <input
                      type="text"
                      name="fabric"
                      value={editFormData.fabric}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={editFormData.price}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Created Date
                    </label>
                    <div className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
                      {editingCollection ? formatDate(editingCollection.created_at) : ''}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Image
                    </label>
                    <div className="space-y-3">
                      {/* Current Image Display */}
                      {editingCollection && editingCollection.image && (
                        <div>
                          <p className="text-xs text-gray-600 mb-2">Current Image:</p>
                          <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={process.env.NODE_ENV === 'production' 
                                ? `https://teenagefashion.in/${editingCollection.image}`
                                : `http://localhost/newfashion/${editingCollection.image}`
                              }
                              alt={editingCollection.title}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAzNkMzNC4yMDkxIDM2IDM2IDM0LjIwOTEgMzYgMzJDMzYgMjkuNzkwOSAzNC4yMDkxIDI4IDMyIDI4QzI5Ljc5MDkgMjggMjggMjkuNzkwOSAyOCAzMkMyOCAzNC4yMDkxIDI5Ljc5MDkgMzYgMzIgMzZaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0yMCA0OEg0NFY0NEM0NCA0Mi44OTU0IDQzLjEwNDYgNDIgNDIgNDJIMjJDMjAuODk1NCA0MiAyMCA0Mi44OTU0IDIwIDQ0VjQ4WiIgZmlsbD0iIzlCOUJBMCIvPgo8L3N2Zz4K';
                              }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* New Image Upload */}
                      <div>
                        <p className="text-xs text-gray-600 mb-2">Upload New Image:</p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">Supported formats: JPG, PNG, WebP</p>
                      </div>
                      
                      {/* Image Preview */}
                      {imagePreview && (
                        <div>
                          <p className="text-xs text-gray-600 mb-2">Preview:</p>
                          <div className="w-32 h-32 rounded-lg overflow-hidden bg-gray-100">
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleEditCancel}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors duration-200"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        isOpen={alertConfig.isOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        type={alertConfig.type}
        onConfirm={alertConfig.onConfirm}
        onCancel={alertConfig.onCancel}
        confirmText={alertConfig.type === 'confirm' ? 'Delete' : 'OK'}
        cancelText="Cancel"
      />
    </div>
  );
} 